import { useLocale as useLocaleR42 } from '@/lib/locale';
// CoachSite.jsx — Phase 113 Pro Coach multi-page public website shell.
// ---------------------------------------------------------------------------
// Renders the premium multi-page website (Home / About / Programs / Team /
// Locations / Contact) for Pro coaches with an enabled landing page and
// `multipage_enabled !== false`. Legacy single-page rendering stays in
// CoachPublic.jsx untouched.
//
// Modes:
//   • public  — real routes /coach/:slug[/about|programs|team|locations|contact]
//   • preview — /instructor/landing-page/preview?page=x (internal nav state)
//   • editor  — preview + ?editor=1 → click-to-edit overlays + postMessage
//     bridge ('sp-edit' section clicks, 'sp-nav' page changes) to the
//     visual editor that hosts this page in an iframe.
//
// Phase 114: Layouts (shape) are applied next to the Theme (skin) as
// data-attributes + CSS vars on the `.csite` scope; nav labels, footer text
// and every section heading resolve through site_copy so coaches can edit
// all of it; the mobile nav sheet is theme-scoped.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { SocialIconLinks } from '@/components/SocialIconLinks';
import { Logo } from '@/components/Logo';
import { getSiteTheme } from '@/lib/siteThemes';
import { getSiteLayout } from '@/lib/siteLayouts';
import { getPageLabel, getCopy } from '@/lib/siteCopy';
import { getPageSections, visiblePages, isPageVisible, PAGE_BY_ID } from '@/lib/coachSiteDefaults';
import { RenderSection } from '@/components/coachsite/siteSections';
import { Menu, Phone, LogIn, ArrowRight } from 'lucide-react';

export default function CoachSite({
    data,
    slug,
    page: pageProp = 'home',
    isPreview = false,
    isParentPreview = false,
    isEditor = false,
    cta,               // { text, disabled, onApply }
    contact,           // { text, visible, onContact }
    wizardPreviewNode, // optional node rendered on the Programs page in preview modes
}) {
    const { t: t42 } = useLocaleR42();
    const navigate = useNavigate();
    const { coach, packages, programs, testimonials, faqs, landing_page: lp } = data;
    const theme = getSiteTheme(lp);
    const layout = getSiteLayout(lp);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    // In preview/editor mode we can't change the URL path — navigation is
    // internal state (seeded from ?page=). Public mode uses real routes.
    const [internalPage, setInternalPage] = useState(pageProp);
    const activePage = isPreview ? internalPage : pageProp;

    useEffect(() => { setInternalPage(pageProp); }, [pageProp]);
    useEffect(() => { window.scrollTo(0, 0); }, [activePage]);

    const nav = (pageId) => {
        setMobileNavOpen(false);
        if (isPreview) {
            setInternalPage(pageId);
            if (isEditor) {
                try { window.parent?.postMessage({ type: 'sp-nav', page: pageId }, window.location.origin); } catch { /* ignore */ }
            }
            return;
        }
        const def = PAGE_BY_ID[pageId];
        navigate(`/coach/${slug}${def?.path ? `/${def.path}` : ''}`);
    };

    const site = useMemo(() => ({
        coach,
        lp,
        packages,
        programs,
        testimonials,
        faqs,
        theme,
        layout,
        slug,
        mode: { isPreview, isParentPreview, isEditor },
        nav,
        cta: {
            text: cta.text,
            disabled: cta.disabled,
            onApply: cta.onApply,
            contactText: contact.text,
            contactVisible: contact.visible,
            onContact: contact.onContact,
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [coach, lp, packages, programs, testimonials, faqs, theme, layout, slug, isPreview, isParentPreview, isEditor, cta.text, cta.disabled, contact.text, contact.visible, activePage]);

    const pages = visiblePages(lp, coach).map((p) => ({ ...p, label: getPageLabel(lp, p.id, p.label) }));
    const sections = getPageSections(lp, activePage)
        .filter((s) => s.visible !== false || isEditor);
    const phoneClean = (lp?.contact_phone || coach?.phone || '').replace(/[^\d+]/g, '');
    const bookText = lp?.book_button_text || 'Book a Lesson';
    const footerTagline = getCopy(site, 'footer', 'tagline');
    const navRight = layout.navStyle === 'right';

    // Guard: if the active page was hidden (e.g. direct URL), fall back home.
    const pageOk = isPageVisible(lp, coach, activePage) || activePage === 'home';

    // Theme + layout tokens live inline on the scope element so they never
    // leak into the SaaS portal. The same object is reused for portaled
    // overlays (mobile nav) that render outside this subtree.
    const scopeVars = { ...theme.vars, ...(layout.vars || {}) };

    return (
        <div
            className="csite min-h-dvh"
            style={scopeVars}
            data-coach-theme={theme.id}
            data-layout={layout.id}
            data-card-style={layout.cardStyle}
            data-testid="coach-site-root"
        >
            {/* ============================ NAVBAR ============================ */}
            <header
                className="sticky top-0 z-40 border-b backdrop-blur supports-[backdrop-filter]:bg-[color:var(--t-surface)]/80"
                style={{ borderColor: 'var(--t-border)', background: 'color-mix(in srgb, var(--t-surface) 88%, transparent)' }}
                data-testid="coach-site-navbar"
            >
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
                    <button type="button" onClick={() => nav('home')} className="csite-focus flex min-w-0 items-center gap-2.5" data-testid="coach-site-brand">
                        {coach.logo_url ? (
                            <img src={coach.logo_url} alt={coach.display_name} className="h-9 w-9 shrink-0 rounded-lg object-cover" />
                        ) : (
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold" style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}>
                                {(coach.display_name || 'SC').slice(0, 1).toUpperCase()}
                            </span>
                        )}
                        <span className="csite-display truncate text-base font-bold sm:text-lg">{coach.display_name}</span>
                    </button>

                    {!navRight && (
                        <nav className="hidden items-center gap-1 md:flex" aria-label={t42("Site pages")}>
                            {pages.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => nav(p.id)}
                                    className={`csite-focus rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${activePage === p.id ? '' : 'hover:bg-black/[0.04]'}`}
                                    style={activePage === p.id ? { color: 'var(--t-primary)', background: 'var(--t-accent-2)' } : { color: 'var(--t-text)' }}
                                    aria-current={activePage === p.id ? 'page' : undefined}
                                    data-testid={`coach-site-nav-${p.id}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </nav>
                    )}

                    <div className="flex items-center gap-2">
                        {navRight && (
                            <nav className="mr-2 hidden items-center gap-5 md:flex" aria-label={t42("Site pages")}>
                                {pages.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => nav(p.id)}
                                        className="csite-focus relative py-2 text-sm font-semibold transition-opacity hover:opacity-100"
                                        style={{ color: 'var(--t-text)', opacity: activePage === p.id ? 1 : 0.7, borderBottom: activePage === p.id ? '2px solid var(--t-primary)' : '2px solid transparent' }}
                                        aria-current={activePage === p.id ? 'page' : undefined}
                                        data-testid={`coach-site-nav-${p.id}`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </nav>
                        )}
                        <Button
                            asChild
                            variant="ghost"
                            className="csite-focus hidden h-10 rounded-xl px-3 text-sm font-semibold sm:inline-flex"
                            data-testid="coach-site-parent-login-link"
                        >
                            <Link to={`/login?from=${encodeURIComponent(`/coach/${slug}`)}`}>
                                <LogIn className="mr-1 h-4 w-4" /> {t42("Log in")} </Link>
                        </Button>
                        <Button
                            onClick={() => cta.onApply()}
                            disabled={cta.disabled}
                            className="csite-btn-primary csite-focus hidden h-10 px-4 text-sm sm:inline-flex"
                            data-testid="coach-site-navbar-book-lesson-button"
                        >
                            {cta.disabled ? cta.text : bookText}
                        </Button>
                        <button
                            type="button"
                            onClick={() => setMobileNavOpen(true)}
                            className="csite-focus flex h-10 w-10 items-center justify-center rounded-xl border md:hidden"
                            style={{ borderColor: 'var(--t-border)' }}
                            aria-label={t42("Open menu")}
                            data-testid="coach-site-mobile-menu-button"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile nav sheet — portaled to <body>, so we re-apply the theme
                scope (class + vars) to keep fonts/colors consistent. */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetContent side="right" className="csite csite-portal flex w-[85vw] max-w-sm flex-col border-l p-0" style={{ ...scopeVars, borderColor: 'var(--t-border)' }} data-testid="coach-site-mobile-nav">
                    <SheetHeader className="border-b px-5 py-4 text-left" style={{ borderColor: 'var(--t-border)' }}>
                        <SheetTitle className="csite-display flex items-center gap-2 text-left text-lg font-bold" style={{ color: 'var(--t-text)' }}>
                            {coach.logo_url ? (
                                <img src={coach.logo_url} alt="" className="h-8 w-8 rounded-lg object-cover" />
                            ) : (
                                <span className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold" style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}>
                                    {(coach.display_name || 'SC').slice(0, 1).toUpperCase()}
                                </span>
                            )}
                            <span className="truncate">{coach.display_name}</span>
                        </SheetTitle>
                    </SheetHeader>
                    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-3" aria-label={t42("Site pages")}>
                        {pages.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => nav(p.id)}
                                className="csite-focus flex items-center justify-between rounded-xl px-4 py-3 text-left text-base font-semibold transition-colors"
                                style={activePage === p.id ? { color: 'var(--t-primary)', background: 'var(--t-accent-2)' } : { color: 'var(--t-text)' }}
                                aria-current={activePage === p.id ? 'page' : undefined}
                                data-testid={`coach-site-mobile-nav-${p.id}`}
                            >
                                {p.label}
                                <ArrowRight className="h-4 w-4 opacity-40" />
                            </button>
                        ))}
                    </nav>
                    <div className="space-y-2 border-t px-4 py-4" style={{ borderColor: 'var(--t-border)', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
                        <Button onClick={() => { setMobileNavOpen(false); cta.onApply(); }} disabled={cta.disabled} className="csite-btn-primary csite-focus h-12 w-full text-base" data-testid="coach-site-mobile-nav-book">
                            {cta.disabled ? cta.text : bookText}
                        </Button>
                        {phoneClean && (
                            <Button asChild className="csite-btn-outline csite-focus h-11 w-full" data-testid="coach-site-mobile-call">
                                <a href={`tel:${phoneClean}`}><Phone className="mr-1.5 h-4 w-4" /> {t42("Call")} {lp?.contact_phone || coach?.phone}</a>
                            </Button>
                        )}
                        <Button asChild variant="ghost" className="csite-focus h-11 w-full text-sm font-semibold" style={{ color: 'var(--t-text)' }} data-testid="coach-site-mobile-login">
                            <Link to={`/login?from=${encodeURIComponent(`/coach/${slug}`)}`}>
                                <LogIn className="mr-1.5 h-4 w-4" /> {t42("Returning parent? Log in")} </Link>
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            {/* ============================ PAGE SECTIONS ============================ */}
            <main data-testid={`coach-site-page-${activePage}`}>
                {!pageOk ? (
                    <div className="mx-auto max-w-xl px-6 py-24 text-center">
                        <h1 className="csite-display text-2xl font-bold">{t42("This page isn’t available")}</h1>
                        <Button onClick={() => nav('home')} className="csite-btn-primary csite-focus mt-5 h-11 px-5">{t42("Back to home")}</Button>
                    </div>
                ) : (
                    sections.map((s) => (
                        <div key={s.id} className={isEditor && s.visible === false ? 'opacity-40' : undefined}>
                            <RenderSection id={s.id} site={site} page={activePage} />
                        </div>
                    ))
                )}
                {activePage === 'programs' && wizardPreviewNode}
            </main>

            {/* ============================ FOOTER ============================ */}
            <footer className="py-10 text-white sm:py-12" style={{ background: 'var(--t-dark-band)' }} data-testid="coach-site-footer">
                <div className="mx-auto grid max-w-6xl grid-cols-2 gap-x-6 gap-y-8 px-4 sm:px-6 md:grid-cols-4 md:gap-8">
                    <div className="col-span-2 md:col-span-1">
                        <div className="csite-display text-lg font-bold">{coach.display_name}</div>
                        {footerTagline && <p className="mt-2 text-sm text-white/60" data-testid="coach-site-footer-tagline">{footerTagline}</p>}
                        <SocialIconLinks coach={coach} size="sm" testIdPrefix="coach-site-footer-social" className="mt-4" />
                    </div>
                    <nav aria-label={t42("Footer pages")} data-testid="coach-site-footer-nav">
                        <div className="text-xs font-bold uppercase tracking-wider text-white/50">{getCopy(site, 'footer', 'pages_label')}</div>
                        <ul className="mt-3 space-y-2 text-sm">
                            {pages.map((p) => (
                                <li key={p.id}>
                                    <button type="button" onClick={() => nav(p.id)} className="text-left text-white/80 hover:text-white" data-testid={`coach-site-footer-nav-${p.id}`}>
                                        {p.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-white/50">{getCopy(site, 'footer', 'contact_label')}</div>
                        <ul className="mt-3 space-y-2 text-sm text-white/80">
                            {phoneClean && <li><a href={`tel:${phoneClean}`} className="hover:text-white" data-testid="coach-site-footer-phone">{lp?.contact_phone || coach?.phone}</a></li>}
                            {coach.pool_name && <li>{coach.pool_name}</li>}
                            <li>
                                <Link to={`/login?from=${encodeURIComponent(`/coach/${slug}`)}`} className="hover:text-white" data-testid="coach-site-footer-login">
                                    {getCopy(site, 'footer', 'login_label')}
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div className="col-span-2 border-t border-white/10 pt-6 md:col-span-1 md:border-0 md:pt-0">
                        <div className="text-xs font-bold uppercase tracking-wider text-white/50">{t42("Legal")}</div>
                        <ul className="mt-3 flex gap-4 text-sm text-white/80 md:flex-col md:gap-2">
                            <li><Link to="/privacy" className="hover:text-white" data-testid="coach-site-footer-privacy">{t42("Privacy")}</Link></li>
                            <li><Link to="/terms" className="hover:text-white" data-testid="coach-site-footer-terms">{t42("Terms")}</Link></li>
                        </ul>
                        <div className="mt-5 flex items-center gap-2 text-xs text-white/50">
                            <Logo light size="sm" />
                        </div>
                        <div className="mt-2 text-xs text-white/40">{t42("Powered by SplashPad · SSA MEDIA LLC")}</div>
                    </div>
                </div>
            </footer>

            {/* ============================ STICKY MOBILE CTA ============================ */}
            <div
                className="fixed inset-x-0 bottom-0 z-30 border-t p-3 backdrop-blur supports-[backdrop-filter]:bg-[color:var(--t-surface)]/80 sm:hidden"
                style={{ borderColor: 'var(--t-border)', background: 'color-mix(in srgb, var(--t-surface) 92%, transparent)', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
                data-testid="coach-site-sticky-cta-bar"
            >
                <div className="flex gap-2">
                    <Button onClick={() => cta.onApply()} disabled={cta.disabled} className="csite-btn-primary csite-focus h-12 flex-1 text-base" data-testid="coach-site-sticky-primary-cta">
                        {cta.text}
                    </Button>
                    {phoneClean && (
                        <Button asChild className="csite-btn-outline csite-focus h-12 w-12 p-0" data-testid="coach-site-sticky-call-cta" aria-label={t42("Call coach")}>
                            <a href={`tel:${phoneClean}`}><Phone className="h-5 w-5" /></a>
                        </Button>
                    )}
                </div>
            </div>
            {/* Spacer so the sticky bar never covers footer content on mobile */}
            <div className="h-20 sm:hidden" aria-hidden />
        </div>
    );
}
